// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import { copyText } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Types --------------------------------------------------------------------------------------
export type TyShareDetails = { title: string; url: string };

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * The public link to one project or one unit, ready to send.
 *
 * **It shows the address rather than copying it silently.** A salesman on a call is about to read
 * this out or paste it into WhatsApp, and a toast saying "copied" leaves them with no way to check
 * what they are about to send. The link is the content of this popup; copying it is one button.
 *
 * `navigator.share` is offered only where it exists — on a phone it opens WhatsApp with the link in
 * it, which is where these go — and the copy button is there either way.
 */
export default function SharePopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { title, url } = (details ?? {}) as TyShareDetails;

    const [isCopied, setIsCopied] = useState(false);
    const canShare = typeof navigator !== "undefined" && !!navigator.share;

    const handleCopy = async () => setIsCopied(await copyText(url));

    const handleShare = async () => {
        try {
            await navigator.share({ title, url });
        } catch {
            // A share sheet the person dismissed is not an error, and there is nothing to report.
        }
    };

    return (
        <section className="side-form side-form--center share">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.common.share}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <p className="share__what">{title}</p>

                <div className="share__link">
                    <span className="share__url" dir="ltr">
                        {url}
                    </span>
                    <button type="button" className="share__copy" onClick={handleCopy}>
                        <Icon name={isCopied ? "check" : "copy"} size={16} />
                        <span>{isCopied ? t.common.copied : t.common.copy}</span>
                    </button>
                </div>

                <p className="side-form__hint">{t.properties.shareHint}</p>
            </div>

            <footer className="side-form__footer">
                <a className="side-form__cancel share__open" href={url} target="_blank" rel="noreferrer">
                    {t.properties.openCatalog}
                </a>

                {canShare && (
                    <button type="button" className="side-form__submit" onClick={handleShare}>
                        {t.common.share}
                    </button>
                )}
            </footer>
        </section>
    );
}
